import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import TableComponent from "./TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import { updateFilters, resetFilters } from "../redux/slices/filterSlice";
import { RootState } from "../redux/slices/store";
import { getData } from "../api/ApiServices";
import "../styles/table-search.css";
import "../styles/ItemModels.css";
import "../styles/modal.css";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/icon-style.css";
import "../styles/common/table-heading-icons.css";
import "../styles/StockSummaryStyles.css";
import "../styles/po-table-styles.css";

const PendingPO: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const filters = useSelector((state: RootState) => state.filters);

  const [pendingPOData, setPendingPOData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [initializePendingPO, setInitializePendingPO] = useState<boolean>(false);


  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "-");
  };


  const calculateExpiryDays = (dateString: string) => {
    if (!dateString) return "";
    
    const dueDate = new Date(dateString);
    const today = new Date();
    
    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays < 0 ? Math.abs(diffDays) : "";
  };

  const columns = [
    {
      key: "poDate",
      label: "PO Date",
      render: (value: any) => formatDisplayDate(value),
      sortable: true,
      filterable: true  
    },
    {
      key: "poNumber",
      label: "PO Number",
      sortable: true,
      filterable: true
    },
    {
      key: "supplierDetails",
      label: "Supplier Name",
      nameKey: "name",
      render: (value: any) => (typeof value === "object" ? value?.name : "N/A"),
      sortable: true,
      filterable: true
    },
    {
      key: "parts",
      label: "Part No",
      nameKey: "internalPartNo",
      render: (value: any) => (typeof value === "object" ? value?.internalPartNo : "N/A"),
      sortable: true,
      filterable: true
    },
    {
      key: "parts",
      label: "Part Name",
      nameKey: "partName",
      render: (value: any) => (typeof value === "object" ? value?.partName : "N/A"),
      sortable: true,
      filterable: true
    },
    {
      key: "parts",
      label: "Category",
      nameKey: "categoryId.name",
      render: (value: any) => (typeof value === "object" ? value?.categoryId?.name : "N/A"),
      sortable: true,
      filterable: true
    },
    {
      key: "qty",
      label: "PO Quantity",
      nameKey:"",
      sortable: true,
      isRightAlign: true,
      filterable: true
    },
    {
      key: "receivedQty",
      label: "Received Quantity",
      // render: (value: any, rowData: any) => {
      //   return rowData.qty - rowData.balanceQty;
      // },
      sortable: true,
      isRightAlign: true,
      filterable: true
    },
    {
      key: "balanceQty",
      label: "Balance Quantity",
      sortable: true,
      isRightAlign: true,
      filterable: true
    },
    {
      key: "dueDate",
      label: "Due Date",
      render: (value: any) => formatDisplayDate(value),
      sortable: true,
      filterable: true,
      filterKey: "dueDate" 
    },
    {
      key: "expiredDays", 
      label: "Expiry Days",
      // render: (value: any, rowData: any) => calculateExpiryDays(rowData.dueDate),
      sortable: true,
      isRightAlign: true,
      filterable: true,
    },
  ];

  const fetchPendingPOData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const queryParams: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        filter: filters
      };

      const response: any = await getData<{
        data: any[];
        total: number;
        pages: number;
      }>("/purchase-orders/pending", queryParams);
      console.log("API Response:", response);

      if (response && response.data) {
        setPendingPOData(response.data.data);
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      } else {
        setPendingPOData([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (err) {
      console.error("Error fetching pending PO data:", err);
      setError("Failed to load pending purchase orders. Please try again later.");
      setPendingPOData([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderValue: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderValue);
  };

  useEffect(() => {
    dispatch(resetFilters());
    setInitializePendingPO(true);
  }, [dispatch]);

  useEffect(() => {
    console.log("filters: ", filters);
    if (initializePendingPO) {
      fetchPendingPOData();
    }
  }, [initializePendingPO, currentPage, itemsPerPage, sortBy, sortOrder, filters]);

  if (isLoading && pendingPOData.length === 0) {
    return (
      <div className="item-models-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const enhancedColumns = columns.map(column => ({
    ...column,
    filterable: true
  }));

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Purchase</span>
              <span>Pending PO</span>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="stock-error-message">{error}</div>}

      <div className="box-container po-table-container">
        {isLoading ? (
          <div className="spinner"></div>
        ) : (
          <>
            <TableComponent
              columns={enhancedColumns}
              data={pendingPOData}
              setFilter={(filters: Record<string, any>) => {
                Object.entries(filters).forEach(([key, value]) => {
                  dispatch(updateFilters({ key, value: String(value) }));
                });
              }}
              setSorting={handleSortChange}
              defaultSortConfig={{ key: sortBy, direction: sortOrder }}
              excludeFields={["status"]}
              context="pendingPO"
              menuTitle="Pending PO"
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              totalItems={totalItems}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PendingPO;